import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { getPlan, type PricingPlanId } from '../data/pricingPlans'

/**
 * The authenticated user's real plan + scan entitlement, read from their
 * profile. This hook derives display state only — every entitlement decision
 * that matters is still enforced by the backend (see `verifyCanScan`).
 */
export interface PlanEntitlement {
  planId: PricingPlanId
  /** "FREE" | "SINGLE SCAN" | "WEEKLY" | "MONTHLY" | "YEARLY" */
  planLabel: string
  /** e.g. "$29.99 / month" */
  priceLabel: string
  /** e.g. "Monthly" */
  billingLabel: string
  /** Unlimited-scan subscription is active. */
  unlimited: boolean
  /** Remaining one-off scan credits. */
  scanCredits: number
  /** Free tier still holding an unused free scan. */
  freeScanAvailable: boolean
  /** The user can start a scan right now. */
  canScan: boolean
  /** Human-readable scan availability, e.g. "1 free scan remaining". */
  accessLabel: string
  /** "Active" | "Free" | "No scans remaining" */
  statusLabel: string
  /** Localised renewal date when the profile exposes one. */
  renewalDate: string | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

type ProfileRow = Record<string, unknown> | null

const SUBSCRIPTION_PLANS: PricingPlanId[] = ['weekly', 'monthly', 'yearly']

/** Maps any legacy or backend plan string onto the four current plan types. */
function normalizePlan(value: unknown): PricingPlanId | null {
  if (typeof value !== 'string') return null
  const raw = value.trim().toLowerCase().replace(/[\s-]+/g, '_')
  switch (raw) {
    case 'free':
      return 'free'
    case 'single':
    case 'single_scan':
    case 'pay_per_scan':
      return 'single_scan'
    case 'weekly':
    case 'week':
      return 'weekly'
    case 'pro':
    case 'truth_seeker':
    case 'monthly':
    case 'month':
      return 'monthly'
    case 'yearly':
    case 'annual':
    case 'year':
      return 'yearly'
    default:
      return null
  }
}

function firstString(profile: ProfileRow, keys: string[]): unknown {
  if (!profile) return null
  for (const key of keys) {
    const value = profile[key]
    if (value !== null && value !== undefined && value !== '') return value
  }
  return null
}

function formatDate(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function usePlanEntitlement(): PlanEntitlement {
  const { user } = useAuth()
  const [profile, setProfile] = useState<ProfileRow>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return
    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
    if (fetchError) throw fetchError
    setProfile(data ?? null)
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      return
    }
    let active = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        await fetchProfile()
      } catch (err) {
        if (active) setError('Could not load your plan')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()

    const channel = supabase
      .channel(`profile-entitlement-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          setProfile(payload.new as Record<string, unknown>)
          setError(null)
        },
      )
      .subscribe()

    return () => {
      active = false
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetchProfile])

  const refresh = useCallback(async () => {
    try {
      await fetchProfile()
      setError(null)
    } catch {
      setError('Could not refresh your plan')
    }
  }, [fetchProfile])

  const isPro = profile?.is_pro === true
  const scanCredits = Number(profile?.scan_credits ?? 0) || 0

  // Plan resolution, most explicit source first. Nothing is hardcoded:
  // a plan column wins, then auth metadata, then the profile's own flags.
  const planId: PricingPlanId =
    normalizePlan(
      firstString(profile, [
        'plan_type',
        'plan',
        'subscription_plan',
        'subscription_type',
        'tier',
      ]),
    ) ??
    normalizePlan(user?.user_metadata?.subscriptionPlan) ??
    (isPro ? 'monthly' : scanCredits > 1 ? 'single_scan' : 'free')

  const plan = getPlan(planId) ?? getPlan('free')!
  const unlimited = SUBSCRIPTION_PLANS.includes(planId) && isPro !== false
  const freeScanAvailable = planId === 'free' && scanCredits > 0
  const canScan = unlimited || scanCredits > 0

  const accessLabel = unlimited
    ? 'Unlimited forensic scans'
    : planId === 'free'
      ? scanCredits > 0
        ? scanCredits === 1
          ? '1 free scan remaining'
          : `${scanCredits} free scans remaining`
        : 'Free scan used'
      : scanCredits > 0
        ? scanCredits === 1
          ? '1 full forensic scan available'
          : `${scanCredits} full forensic scans available`
        : 'No scans remaining'

  const statusLabel = unlimited
    ? 'Active'
    : canScan
      ? planId === 'free'
        ? 'Free'
        : 'Active'
      : 'No scans remaining'

  const renewalDate = unlimited
    ? formatDate(
        firstString(profile, [
          'current_period_end',
          'subscription_ends_at',
          'renews_at',
          'next_billing_date',
        ]),
      )
    : null

  return {
    planId,
    planLabel: plan.label,
    priceLabel: plan.priceLabel,
    billingLabel: plan.billing,
    unlimited,
    scanCredits,
    freeScanAvailable,
    canScan,
    accessLabel,
    statusLabel,
    renewalDate,
    loading,
    error,
    refresh,
  }
}
